import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';

/**
 * Landing page for a magic / share link (`/auth/magic?token=…`). Reads the token
 * from the query string, redeems it for a session, then bounces to the library.
 * A GET link can't carry our CSRF header, so redemption happens here via a POST
 * the SPA makes — the link itself only needs to open this page.
 */
export function MagicPage() {
  const [status, setStatus] = useState<'working' | 'error'>('working');
  const [message, setMessage] = useState(t('magicPage.completingSignIn'));
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 StrictMode double-invoke — token is single-use
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get('token') ?? '';
    if (!token) {
      setStatus('error');
      setMessage(t('magicPage.noTokenFound'));
      return;
    }
    api
      .consumeMagicToken(token)
      .then(() => window.location.assign('/'))
      .catch((err) => {
        setStatus('error');
        setMessage(err instanceof ApiError ? err.message : t('magicPage.signInFailed'));
      });
  }, []);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-white px-4">
      <div className="w-full max-w-[400px] rounded-2xl bg-white p-10 text-center text-gray-900 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="mb-6 flex justify-center">
          <img src="/aicotone-logo.png" alt="AIcotone" className="h-12 w-auto" />
        </div>
        {status === 'working' ? (
          <p className="text-sm text-gray-600">{message}</p>
        ) : (
          <>
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">{message}</p>
            <a href="/login" className="text-[13px] font-medium text-brand-600 hover:text-brand-700">{t('magicPage.toSignIn')}</a>
          </>
        )}
      </div>
    </div>
  );
}
